import React, { useEffect, useRef } from 'react'

import { AnyAction, Dispatch } from 'redux'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { differenceInDays } from 'date-fns'
import { v4 as uuidv4 } from 'uuid'
import qs from 'qs'

import { reset } from '@store/refinanciamento/actions'
import routesV2 from '@routes/routes'
import { Page } from '@templates'
import { GridStyled, Loading } from '@atoms'
import { BridgeService } from '@services/bridge'
import { ApplicationState } from '@store/types'
import { useMobileUserInfo } from '@hooks'
import { WbEnvironments } from '@interco/inter-webview-bridge'
import * as navigationActions from '@store/ui/navigation/actions'
import * as sessionActions from '@store/session/actions'
import * as dadosPessoaisActions from '@store/refinanciamento/dadosPessoais/actions'
import * as dadosBancariosActions from '@store/refinanciamento/dadosBancarios/actions'
import * as contatoActions from '@store/refinanciamento/contato/actions'
import { timestampToDatePt } from '@utils/functions'
import useAuth from '@hooks/useAuth'
import { authenticationRequest } from '@store/refinanciamento/authentication/actions'
import { initTrackRefin } from '@utils/trackRefin'

import {
  parseIsApp,
  getUtmSourceFromParams,
  getUtmMediumFromParams,
  getUtmCampaignFromParams,
  getUtmContentFromParams,
  handleRefinancingBegin,
} from './helpers'

interface RootPageParams {
  isApp: boolean
  origemFromParams: string
  origemGoBackFromParams: string
  accessTokenFromParams: string
  facebookIdFromParams: string
  gaClientIdFromParams: string
  gaClidFromParams: string
  userNameFromParams: string
  cpfFromParams: string
  campanhaFromParams: string
  sangueLaranjaFromParams: boolean
  utmSource: string
  utmMedium: string
  utmCampaign: string
  utmContent: string
}

interface MobileUserInfo {
  cellPhoneNumber?: string
  email?: string
  cpf?: string
  name?: string
  account?: string
  birthDate?: string | number
}

const parseRootPageParams = (search: string): RootPageParams => {
  const params = qs.parse(search, { ignoreQueryPrefix: true })
  const origemFromParams = params?.origem?.toString() ?? 'site'
  const origemGoBackFromParams = params?.origemgoback?.toString() ?? ''
  const accessTokenFromParams = params?.accessToken?.toString() ?? ''
  const facebookIdFromParams = params?.fbclid?.toString() ?? ''
  const gaClientIdFromParams = params?.gaid?.toString() ?? ''
  const gaClidFromParams = params?.gclid?.toString() ?? ''
  const userNameFromParams = params?.username?.toString() ?? ''
  const cpfFromParams = params?.document?.toString() ?? ''
  const campanhaFromParams = params?.campanha?.toString() ?? ''
  const sangueLaranjaFromParams = parseIsApp(params?.sangueLaranja?.toString()) ?? false
  const isAppFromParams = params?.isApp?.toString() ?? ''
  const isAppParsed = parseIsApp(isAppFromParams)
  const origemParsed = parseIsApp(origemFromParams)
  const isAppFromBridge = !BridgeService.isBrowser()
  const isApp = isAppParsed ?? (isAppFromBridge ? true : origemParsed ?? false)

  return {
    isApp,
    origemFromParams,
    origemGoBackFromParams,
    accessTokenFromParams,
    facebookIdFromParams,
    gaClientIdFromParams,
    gaClidFromParams,
    userNameFromParams,
    cpfFromParams,
    campanhaFromParams,
    sangueLaranjaFromParams,
    utmSource: getUtmSourceFromParams(params),
    utmMedium: getUtmMediumFromParams(params),
    utmCampaign: getUtmCampaignFromParams(params),
    utmContent: getUtmContentFromParams(params),
  }
}

const syncStoreWithParams = (dispatch: Dispatch<AnyAction>, params: RootPageParams) => {
  dispatch(navigationActions.setIsApp(params.isApp))
  dispatch(navigationActions.setOrigem(params.origemFromParams))
  dispatch(navigationActions.setOrigemGoBack(params.origemGoBackFromParams))
  dispatch(sessionActions.setAccessToken(params.accessTokenFromParams))
  dispatch(sessionActions.setFacebookId(params.facebookIdFromParams))
  dispatch(sessionActions.setGaClientId(params.gaClientIdFromParams))
  dispatch(sessionActions.setGaClid(params.gaClidFromParams))
  dispatch(sessionActions.setUserNameSession(params.userNameFromParams))
  dispatch(sessionActions.setCpfSession(params.cpfFromParams))
  dispatch(sessionActions.setCampanha(params.campanhaFromParams))
  dispatch(sessionActions.setSangueLaranja(params.sangueLaranjaFromParams))
  dispatch(sessionActions.setUtmSource(params.utmSource))
  dispatch(sessionActions.setUtmMedium(params.utmMedium))
  dispatch(sessionActions.setUtmCampaign(params.utmCampaign))
  dispatch(sessionActions.setUtmContent(params.utmContent))
}

const syncStoreWithUserInfo = (dispatch: Dispatch<AnyAction>, userInfo: MobileUserInfo) => {
  dispatch(contatoActions.setTelefone(userInfo.cellPhoneNumber ?? ''))
  dispatch(contatoActions.setEmail(userInfo.email ?? ''))
  dispatch(dadosPessoaisActions.setCpfDadosPessoais(userInfo.cpf ?? ''))
  dispatch(dadosPessoaisActions.setUserNameDadosPessoais(userInfo.name ?? ''))
  dispatch(dadosPessoaisActions.setContaDadosPessoais(userInfo.account ?? ''))
  dispatch(dadosBancariosActions.setContaDadosBancarios(userInfo.account ?? ''))
  dispatch(
    dadosPessoaisActions.setDataNascimento(timestampToDatePt(Number(userInfo.birthDate)) ?? ''),
  )
}

const runInitFlow = ({
  isApp,
  hasStarted,
  hasUserInfo,
  hasAccessToken,
  dispatch,
  navigate,
  authorize,
  markAsStarted,
}: {
  isApp: boolean
  hasStarted: boolean
  hasUserInfo: boolean
  hasAccessToken: boolean
  dispatch: Dispatch<AnyAction>
  navigate: ReturnType<typeof useNavigate>
  authorize: () => void
  markAsStarted: () => void
}) => {
  if (hasStarted) return

  if (isApp) {
    if (!hasUserInfo) return
    markAsStarted()
    handleRefinancingBegin(dispatch, navigate)
    return
  }

  markAsStarted()

  if (!hasAccessToken) {
    Promise.resolve(authorize()).catch(() => undefined)
    return
  }

  dispatch(authenticationRequest())
  navigate(routesV2.VERIFICACAO, { replace: true })
}

const RootPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { sessionId, sessionCreateDate } = useSelector((state: ApplicationState) => state.session)
  const isApp = useSelector((state: ApplicationState) => state.ui.navigation.isApp)
  const userInfo = useMobileUserInfo()
  const { authorize } = useAuth()
  const hasStarted = useRef(false)

  useEffect(() => {
    dispatch(reset())
  }, [dispatch])

  useEffect(() => {
    const isExpired =
      !sessionId ||
      !sessionCreateDate ||
      differenceInDays(new Date(), new Date(sessionCreateDate)) > 0

    const id = isExpired ? uuidv4() : sessionId

    if (isExpired) {
      dispatch(sessionActions.setSessionId(id))
    }

    initTrackRefin(id, isApp)
  }, [dispatch, sessionCreateDate, sessionId, isApp])

  useEffect(() => {
    const params = parseRootPageParams(location?.search ?? '')

    syncStoreWithParams(dispatch, params)

    if (params.isApp && userInfo) {
      syncStoreWithUserInfo(dispatch, userInfo)
    }

    runInitFlow({
      isApp: params.isApp,
      hasStarted: hasStarted.current,
      hasUserInfo: Boolean(userInfo),
      hasAccessToken: Boolean(params.accessTokenFromParams),
      dispatch,
      navigate,
      authorize,
      markAsStarted: () => {
        hasStarted.current = true
      },
    })
  }, [dispatch, navigate, authorize, userInfo, location?.search])

  return (
    <Page>
      <GridStyled>
        {BridgeService.getEnvironment() === WbEnvironments.ANDROID ? (
          <Loading width={50} height={50} />
        ) : (
          <></>
        )}
      </GridStyled>
    </Page>
  )
}

export default RootPage
